import React from "react";
import { Link } from "react-router-dom";
import PolicyTemplate from "./PolicyTemplate";
import siteConfig from "../config/siteConfig";
import "../css/policycenter.scss";

const email = siteConfig.contact.email;
const brand = siteConfig.brandName;
const toTop = () => window.scrollTo({ top: 0, behavior: "auto" });

export const policyGroups = [
  {
    title: "Shopping & orders",
    description: "What to expect before, during, and after a purchase.",
    items: [
      { to: "/shipping", label: "Shipping information", summary: "Processing, tracking, international delivery, and delays." },
      { to: "/returns", label: "Returns & exchanges", summary: "Eligibility, refunds, exchanges, and faulty items." },
      { to: "/payments", label: "Payments & billing", summary: "Charges, currency, taxes, verification, and refunds." },
      { to: "/order-changes", label: "Order changes & cancellations", summary: "Correction windows, cancellation requests, and refused orders." },
      { to: "/preorders", label: "Pre-orders & backorders", summary: "Estimated dates, mixed orders, delays, and cancellations." },
      { to: "/warranty", label: "Product warranty & care", summary: "Defects, wear, care, remedies, and statutory rights." },
      { to: "/gift-cards", label: "Gift cards & store credit", summary: "Use, balances, refunds, expiry, and fraud protection." },
      { to: "/promotions", label: "Promotions & discounts", summary: "Codes, exclusions, pricing errors, and fair-use rules." },
    ],
  },
  {
    title: "Privacy & digital",
    description: "How the storefront handles data, storage, and security.",
    items: [
      { to: "/privacy", label: "Privacy policy", summary: "Collection, use, sharing, retention, and international transfers." },
      { to: "/cookies", label: "Cookie policy", summary: "The exact browser storage used by this storefront." },
      { to: "/privacy-rights", label: "Privacy choices & requests", summary: "Access, correction, deletion, objections, and appeals." },
      { to: "/security", label: "Security disclosure", summary: "How to report a suspected vulnerability responsibly." },
    ],
  },
  {
    title: "Legal & responsibility",
    description: "Rules for using the site and our commitments to customers.",
    items: [
      { to: "/terms", label: "Terms of service", summary: "The agreement governing use of the site and purchases." },
      { to: "/accessibility", label: "Accessibility statement", summary: "Our accessibility aim, measures, limits, and feedback route." },
      { to: "/intellectual-property", label: "Intellectual property notices", summary: "Permitted use and good-faith infringement reports." },
      { to: "/complaints", label: "Complaints & resolution", summary: "A clear review, response, and escalation process." },
      { to: "/product-safety", label: "Product safety & recalls", summary: "Safe use, incident reports, corrective action, and recalls." },
      { to: "/sustainability", label: "Packaging & sustainability", summary: "Careful claims, disposal guidance, and ongoing improvements." },
      { to: "/ethical-sourcing", label: "Supplier & ethical sourcing", summary: "Minimum expectations for labor, materials, and remediation." },
      { to: "/legal-notice", label: "Legal notice", summary: "Store identity, formal communications, and legal disclosures." },
    ],
  },
];

export const storePolicies = {
  payments: {
    eyebrow: "Shopping & orders",
    title: "Payments & billing",
    intro: `This policy explains how prices, payment authorization, billing checks, taxes, and refunds work when you purchase from ${brand}.`,
    sections: [
      {
        id: "payment-methods",
        title: "Accepted payment methods",
        paragraphs: [
          "The payment methods currently available are displayed at checkout. Availability can depend on your billing country, device, currency, and the payment provider. Please use a payment method that you are authorized to use.",
          "Card numbers and security codes are entered on the payment provider’s encrypted page. This storefront does not receive or store complete card credentials.",
        ],
      },
      {
        id: "payment-authorization",
        title: "Authorization and capture",
        paragraphs: [
          "Submitting payment authorizes the payment provider and your issuer to validate the transaction. Your statement may show a pending authorization before the charge is completed. An unsuccessful or cancelled order can still leave a temporary authorization that only the issuer can release.",
          "We do not accept an order until dispatch is confirmed. If payment was collected for an order we cannot accept, it will be reversed or refunded to the original method.",
        ],
      },
      {
        id: "payment-prices",
        title: "Prices, currency, and taxes",
        paragraphs: [
          `Unless a product page says otherwise, prices are presented in ${siteConfig.currency.code}. Delivery charges and any taxes collected by us are shown before payment. Your bank may apply currency conversion or international transaction fees that we do not control.`,
          "Import duties, customs assessments, and local handling charges that are not collected at checkout remain the customer’s responsibility unless applicable law requires otherwise.",
        ],
      },
      {
        id: "payment-verification",
        title: "Verification and fraud prevention",
        paragraphs: [
          "We or the payment provider may ask for reasonable information to confirm the purchaser, billing details, delivery destination, or authority to use a payment method. We may pause, limit, or cancel an order when a payment cannot be verified or appears unauthorized.",
          "Verification documents are used only for the review, retained only as needed, and handled under our privacy policy.",
        ],
        links: [{ prefix: "Read our ", to: "/privacy", label: "privacy policy", suffix: " for details." }],
      },
      {
        id: "payment-errors",
        title: "Pricing and billing errors",
        paragraphs: [
          "If a price, discount, tax, delivery charge, or total is clearly incorrect, we may correct it before accepting the order. If payment has already been taken, we will ask whether you wish to continue at the corrected amount or receive a cancellation and refund.",
          "Contact us promptly if a receipt or statement does not match your order. Please do not send full card numbers or security codes by email.",
        ],
      },
      {
        id: "payment-refunds",
        title: "Refunds and disputes",
        paragraphs: [
          "Approved refunds are returned to the original payment method. Bank processing times are outside our control, and currency movements or issuer fees may cause the posted amount to differ from the original statement amount.",
          "If you do not recognize a charge, contact us first so we can investigate. This does not limit your right to contact your issuer or use any chargeback or consumer remedy available by law.",
        ],
        email: true,
      },
    ],
    related: [
      { to: "/returns", label: "Returns & refunds" },
      { to: "/order-changes", label: "Order changes" },
      { to: "/privacy", label: "Privacy policy" },
    ],
  },
  orders: {
    eyebrow: "Shopping & orders",
    title: "Order changes & cancellations",
    intro: "Orders move into preparation quickly. This policy explains what can be changed, when cancellation may still be possible, and what happens if we cannot fulfill an order.",
    sections: [
      {
        id: "orders-review",
        title: "Review before payment",
        paragraphs: [
          "Please confirm product, size, color, quantity, email, and delivery address before paying. An order confirmation acknowledges receipt of your request; it does not by itself mean that the order has been accepted or dispatched.",
        ],
      },
      {
        id: "orders-request",
        title: "Requesting a change",
        paragraphs: [
          "Send the order number and requested correction as soon as possible. We will try to help before packing begins, but cannot guarantee changes to products, quantities, addresses, delivery methods, or gift notes after submission.",
          "For security, we may require a new order when a request changes the purchaser, payment amount, country, or destination materially.",
        ],
        email: true,
      },
      {
        id: "orders-cancel",
        title: "Cancellation requests",
        paragraphs: [
          "You may request cancellation before dispatch. If the order has already entered packing, been personalized, or shipped, the request may be declined and the returns policy will apply after delivery.",
          "A cancellation is effective only when we confirm it. Mandatory cancellation or withdrawal rights under local law continue to apply even where this operational window has closed.",
        ],
      },
      {
        id: "orders-address",
        title: "Address problems",
        paragraphs: [
          "We cannot redirect a parcel once a carrier prevents changes. If a parcel is returned because an address was incomplete, refused, or unclaimed, we will contact you about reshipment or refund options. Additional carrier and delivery costs may be deducted where permitted by law.",
        ],
      },
      {
        id: "orders-our-cancellation",
        title: "When we may cancel",
        paragraphs: [
          "We may cancel or limit an order because of unavailable stock, a material listing or pricing error, payment failure, suspected fraud, unlawful destination, purchase-limit abuse, or an event outside reasonable control.",
          "If we cancel after collecting payment, we will return the affected amount to the original method. We will not substitute a materially different item without your agreement.",
        ],
      },
      {
        id: "orders-rights",
        title: "Consumer rights",
        paragraphs: [
          "Nothing in this policy removes rights that cannot be waived under the law that applies to you, including remedies for goods that are faulty, unsafe, not as described, or not supplied.",
        ],
        links: [{ prefix: "See also our ", to: "/returns", label: "returns policy", suffix: "." }],
      },
    ],
    related: [
      { to: "/shipping", label: "Shipping information" },
      { to: "/returns", label: "Returns & exchanges" },
      { to: "/payments", label: "Payments & billing" },
    ],
  },
  preorders: {
    eyebrow: "Shopping & orders",
    title: "Pre-orders & backorders",
    intro: "This policy applies whenever a product is clearly marked pre-order, made-to-order, coming soon, or backordered before you add it to your cart.",
    sections: [
      {
        id: "preorder-labels",
        title: "Product labels and estimates",
        paragraphs: [
          "The product page will identify non-immediate inventory and provide the best available dispatch estimate. Estimates are not guaranteed delivery dates and can change because of production, quality review, freight, customs, or supplier delays.",
        ],
      },
      {
        id: "preorder-payment",
        title: "Payment timing",
        paragraphs: [
          "Unless checkout states otherwise, payment is authorized or collected when the order is placed so inventory can be reserved. If the payment provider only authorizes the amount, a later capture or renewed authorization may be required before dispatch.",
        ],
      },
      {
        id: "preorder-mixed",
        title: "Mixed orders",
        paragraphs: [
          "An order containing both in-stock and delayed items may be held until every item is ready. If split shipping is offered, any additional charge will be shown or agreed before we send separate parcels.",
        ],
      },
      {
        id: "preorder-changes",
        title: "Changes and cancellation",
        paragraphs: [
          "You may request a pre-order cancellation before dispatch unless the item is personalized or has entered production specifically for you. We will explain any lawful exception before taking the order.",
          "Changing a reserved variant depends on stock. We will never move you to a different size, color, or edition without your approval.",
        ],
      },
      {
        id: "preorder-delays",
        title: "Material delays or cancellation",
        paragraphs: [
          "If the expected dispatch changes materially, we will use the contact information on the order to provide an update and, where appropriate, a revised estimate or cancellation option. If we cannot supply the item, we will refund the affected amount.",
        ],
      },
      {
        id: "preorder-returns",
        title: "Returns and mandatory rights",
        paragraphs: [
          "Once delivered, an eligible pre-order follows the same returns process as an in-stock item. No part of this policy limits rights for faulty or misdescribed goods or a statutory withdrawal right that applies in your location.",
        ],
      },
    ],
    related: [
      { to: "/order-changes", label: "Order changes" },
      { to: "/shipping", label: "Shipping information" },
      { to: "/returns", label: "Returns & exchanges" },
    ],
  },
  warranty: {
    eyebrow: "Shopping & orders",
    title: "Product warranty & care",
    intro: `We want every ${brand} item to arrive as described and remain useful with reasonable care. This policy explains defect reviews, exclusions, and available remedies.`,
    sections: [
      {
        id: "warranty-statutory",
        title: "Your statutory rights",
        paragraphs: [
          "Goods must meet any non-excludable standards that apply in your location, including requirements relating to title, description, quality, fitness, durability, and safety. These rights are separate from and take priority over any voluntary warranty described here.",
        ],
      },
      {
        id: "warranty-coverage",
        title: "What we review",
        paragraphs: [
          "We review credible reports of defects in materials, construction, printing, binding, hardware, or finish that were present at delivery or appeared during reasonable use. Natural grain, small color variation, screen-to-product color differences, and other disclosed characteristics are not automatically defects.",
        ],
      },
      {
        id: "warranty-exclusions",
        title: "Wear and exclusions",
        bullets: [
          "ordinary wear, patina, fading, scratches, dents, and consumable depletion;",
          "damage from misuse, overfilling, incorrect installation, liquid, heat, chemicals, or unsuitable storage;",
          "alteration, unauthorized repair, or use with incompatible accessories; and",
          "issues disclosed before purchase or caused after risk lawfully passed to the customer.",
        ],
        paragraphs: [
          "An exclusion is applied only where it is relevant to the reported issue and permitted by law.",
        ],
      },
      {
        id: "warranty-care",
        title: "Reasonable care",
        paragraphs: [
          "Follow care information on the product page or packaging. Keep paper dry, avoid overloading rings or discs, test writing tools on an inconspicuous page, and store covers away from sustained heat, moisture, pressure, and direct sunlight.",
        ],
      },
      {
        id: "warranty-claim",
        title: "How to report a problem",
        paragraphs: [
          "Contact us with the order number, product name, description of the issue, when it appeared, and clear photographs or video. Do not discard the item or packaging until we confirm whether inspection or return is needed.",
        ],
        email: true,
      },
      {
        id: "warranty-remedies",
        title: "Assessment and remedies",
        paragraphs: [
          "After review, a remedy may include care guidance, repair, replacement, partial refund, full refund, or another remedy required by law. Availability, proportionality, product age, and the nature of the problem may affect which remedy is appropriate. We cover reasonable return costs when the item is confirmed faulty and the law or our decision requires return.",
        ],
      },
    ],
    related: [
      { to: "/returns", label: "Returns & exchanges" },
      { to: "/product-safety", label: "Product safety" },
      { to: "/contact", label: "Contact client care" },
    ],
  },
  giftCards: {
    eyebrow: "Shopping & orders",
    title: "Gift cards & store credit",
    intro: "These terms apply if digital gift cards, promotional credit, or return credit are offered through the storefront. Product-specific terms shown at issue also apply.",
    sections: [
      {
        id: "gift-availability",
        title: "Availability and delivery",
        paragraphs: [
          "Gift cards are available only when a gift-card product appears for sale. Digital delivery is sent to the email entered by the purchaser or recipient. Please review the address carefully; timing can be affected by payment review or email filtering.",
        ],
      },
      {
        id: "gift-use",
        title: "Using a balance",
        paragraphs: [
          "A valid code can be applied to eligible purchases on the issuing storefront. More than one use may be permitted until the balance reaches zero, subject to the checkout options shown. Gift cards cannot be used to buy another gift card unless expressly allowed.",
        ],
      },
      {
        id: "gift-expiry",
        title: "Expiry and fees",
        paragraphs: [
          "Any expiry date or maintenance fee will be disclosed before purchase and will apply only where lawful. If no expiry is disclosed at sale, we will not later add one. Mandatory local rules about stored value always prevail.",
        ],
      },
      {
        id: "gift-refunds",
        title: "Refunds and cash redemption",
        paragraphs: [
          "Gift cards and store credit are not refundable or redeemable for cash except where required by law. A refund for goods bought with a gift card is ordinarily returned to gift-card or store-credit balance; mixed payments may be returned proportionally to the original methods.",
        ],
      },
      {
        id: "gift-risk",
        title: "Lost codes and fraud",
        paragraphs: [
          "Treat a gift-card code like cash. We may freeze a balance while investigating credible evidence of theft, unauthorized resale, payment reversal, automated code testing, or other misuse. If you report a lost code, we may be able to replace the remaining balance after verifying the original purchase, but replacement is not guaranteed.",
        ],
      },
      {
        id: "gift-support",
        title: "Balance and support",
        paragraphs: [
          "For help with delivery, balance, or an error, include the purchase order number and only the last four characters of the code. Never publish or email the full code unless our support team provides a secure method.",
        ],
        email: true,
      },
    ],
    related: [
      { to: "/payments", label: "Payments & billing" },
      { to: "/promotions", label: "Promotions & discounts" },
      { to: "/returns", label: "Returns & exchanges" },
    ],
  },
  promotions: {
    eyebrow: "Shopping & orders",
    title: "Promotions & discounts",
    updated: "October 23, 2026",
    intro: "This policy keeps promotional offers clear and fair. The specific terms displayed with an offer form part of this policy and take priority if they are more specific.",
    sections: [
      {
        id: "promo-terms",
        title: "Offer terms",
        paragraphs: [
          "An offer may specify a start and end time, eligible products, minimum spend, customer or region eligibility, redemption limit, code, and excluded charges. Times use the zone stated with the offer; if none is stated, contact client care before relying on a deadline.",
        ],
      },
      {
        id: "promo-codes",
        title: "Using a code",
        paragraphs: [
          "Enter the code before payment and confirm that the total changed. Codes normally cannot be stacked, exchanged for cash, added after an order is submitted, or applied to gift cards, delivery, taxes, prior purchases, or excluded collections unless the offer states otherwise.",
        ],
      },
      {
        id: "promo-limits",
        title: "Limits and fair use",
        paragraphs: [
          "We may enforce stated per-person, per-household, per-email, per-payment-method, or per-address limits. We may reject attempts to obtain an unintended benefit through automation, duplicate identities, manipulated carts, code publication contrary to an offer, or other abuse.",
        ],
      },
      {
        id: "promo-returns",
        title: "Returns from a promotion",
        paragraphs: [
          "Refunds reflect the amount actually paid for the returned item. If a return makes the retained order ineligible for a bundle, threshold gift, or tiered discount, the refund may be recalculated or the promotional item may also need to be returned, where clearly disclosed and lawful.",
        ],
      },
      {
        id: "promo-pricing",
        title: "Price changes and errors",
        paragraphs: [
          "Prices can change prospectively. We do not offer retroactive price matching unless an offer expressly says so. A manifest technical or listing error does not require us to supply at the incorrect price; if it affects a submitted order, we will offer a corrected purchase or cancellation and refund.",
        ],
      },
      {
        id: "promo-changes",
        title: "Changes and mandatory rights",
        paragraphs: [
          "We may pause or amend an offer before an order is accepted when fraud, error, stock failure, or circumstances outside reasonable control make continuation impracticable. We will not change an accepted order retroactively, and nothing here limits mandatory consumer rights.",
        ],
      },
    ],
    related: [
      { to: "/payments", label: "Payments & billing" },
      { to: "/returns", label: "Returns & exchanges" },
      { to: "/terms", label: "Terms of service" },
    ],
  },
  accessibility: {
    eyebrow: "Legal & responsibility",
    title: "Accessibility statement",
    intro: `${brand} is working toward an inclusive shopping experience that can be perceived, understood, navigated, and used by as many people as possible.`,
    sections: [
      {
        id: "accessibility-goal",
        title: "Our accessibility goal",
        paragraphs: [
          "We use the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA as our current design and improvement target. This is an aim and working standard, not a claim that every page or third-party service fully conforms today.",
        ],
        links: [{ prefix: "Learn about ", to: "https://www.w3.org/TR/WCAG22/", label: "WCAG 2.2", suffix: ".", external: true }],
      },
      {
        id: "accessibility-measures",
        title: "Measures we take",
        bullets: [
          "semantic headings, labels, landmarks, and descriptive controls;",
          "keyboard navigation, visible focus, and a skip-to-content link;",
          "text alternatives for meaningful images and restrained animation;",
          "responsive layouts, readable contrast, and browser zoom support; and",
          "clear validation messages for forms and checkout fields.",
        ],
      },
      {
        id: "accessibility-compatibility",
        title: "Compatibility",
        paragraphs: [
          "The site is designed for current versions of major browsers and common assistive technologies on desktop and mobile. Very old browsers, custom style overrides, or disabled JavaScript may reduce functionality, particularly for the cart and checkout.",
        ],
      },
      {
        id: "accessibility-limitations",
        title: "Known limitations",
        paragraphs: [
          "Some payment, carrier, or other third-party pages are controlled by their providers and may not offer the same experience as this site. Product imagery may communicate subtle color or texture that text cannot reproduce completely. We provide product descriptions and support as alternatives and continue to review these areas.",
        ],
      },
      {
        id: "accessibility-feedback",
        title: "Feedback and assistance",
        paragraphs: [
          "If a barrier prevents you from finding information, selecting a product, completing an order, or reading a policy, tell us the page, task, device, browser, and assistive technology involved. Please do not include payment card details. We will offer the information or service through a reasonable alternative where possible.",
        ],
        email: true,
        emailSuffix: ". Use the subject “Accessibility feedback”.",
      },
      {
        id: "accessibility-review",
        title: "Review process",
        paragraphs: [
          "Accessibility is reviewed through self-evaluation, keyboard checks, automated tools, and feedback from customers. We prioritize issues that block purchases, account or payment access, safety information, and customer support.",
        ],
      },
    ],
    related: [
      { to: "/contact", label: "Contact client care" },
      { to: "/policies", label: "All policies" },
      { to: "/terms", label: "Terms of service" },
    ],
  },
  privacyRights: {
    eyebrow: "Privacy & digital",
    title: "Privacy choices & requests",
    updated: "October 23, 2026",
    intro: "This page gives you a practical way to exercise privacy choices. The rights available and any exceptions depend on the law that applies to your request.",
    sections: [
      {
        id: "rights-available",
        title: "Requests you can make",
        bullets: [
          "ask whether we hold personal information about you and request access or a portable copy;",
          "ask us to correct inaccurate or incomplete information;",
          "ask us to delete information, subject to lawful retention needs;",
          "object to or restrict certain processing, or withdraw consent for future use;",
          "opt out of direct marketing at any time; and",
          "raise a concern or appeal a decision where applicable law provides that option.",
        ],
      },
      {
        id: "rights-submit",
        title: "How to submit a request",
        paragraphs: [
          "Email us from the address associated with your order when possible. Use the subject “Privacy request” and state the right you wish to exercise, your country or region, and enough order or contact information for us to locate relevant records. Do not send government identification unless we specifically request it through an appropriate method.",
        ],
        email: true,
      },
      {
        id: "rights-verify",
        title: "Verification and authorized agents",
        paragraphs: [
          "We verify requests in a way proportionate to the information and risk. We may ask you to confirm an email, order detail, or signed authority. An authorized agent may submit a request where local law allows, but we may confirm the agent’s authority and your identity directly.",
        ],
      },
      {
        id: "rights-response",
        title: "Timing, fees, and decisions",
        paragraphs: [
          "We will acknowledge and respond within the period required by applicable law. We may extend the response where law permits and will explain the reason. Requests are normally free, although a reasonable fee or refusal may apply to manifestly unfounded, excessive, or repetitive requests where permitted.",
          "If we cannot fulfill all or part of a request, we will explain the applicable reason and any available appeal or complaint route, unless the law prevents that explanation.",
        ],
      },
      {
        id: "rights-sale",
        title: "Sale, sharing, and targeted advertising",
        paragraphs: [
          "We do not currently sell personal information for money or share it for cross-context behavioral advertising, and this storefront does not currently run targeted-advertising technology. We also do not use sensitive personal information to infer personal characteristics. If these practices change, we will update our notices and provide any legally required opt-out mechanism before the change applies.",
        ],
      },
      {
        id: "rights-regional",
        title: "Regional information",
        paragraphs: [
          "People in Mexico may exercise rights of access, rectification, cancellation, and opposition (ARCO rights), subject to the law applicable to the request. Residents of the EEA or United Kingdom may also have rights relating to lawful basis, restriction, portability, objection, and supervisory-authority complaints. Residents of certain U.S. states may have rights to know, correct, delete, obtain a copy, opt out of specified uses, limit certain sensitive-data use, and receive equal service for exercising a right.",
          "A regional law may not apply to every business or request. We will apply the rights that govern the circumstances and may voluntarily honor a request more broadly when practical.",
        ],
      },
      {
        id: "rights-nonretaliation",
        title: "No retaliation",
        paragraphs: [
          "We will not unlawfully discriminate or retaliate because you exercised a privacy right. A request to delete information needed for a requested service may affect our ability to continue that service, and we will explain the practical consequence before acting where appropriate.",
        ],
      },
    ],
    related: [
      { to: "/privacy", label: "Privacy policy" },
      { to: "/cookies", label: "Cookie policy" },
      { to: "/security", label: "Security disclosure" },
    ],
  },
  intellectualProperty: {
    eyebrow: "Legal & responsibility",
    title: "Intellectual property notices",
    intro: "We respect creative rights and expect the same from site visitors. This page explains permitted use and a good-faith process for reporting content that may infringe another person’s rights.",
    sections: [
      {
        id: "ip-ownership",
        title: "Site materials",
        paragraphs: [
          `Unless stated otherwise, original site text, product presentation, graphics, photographs, illustrations, layout, and branding are owned by ${brand} or used with permission. Third-party names and marks remain the property of their respective owners.`,
        ],
      },
      {
        id: "ip-permitted",
        title: "Permitted personal use",
        paragraphs: [
          "You may view the site, save order records, and print reasonable extracts for personal, non-commercial shopping and reference. This limited permission does not transfer ownership and ends if you breach these terms.",
        ],
      },
      {
        id: "ip-restrictions",
        title: "Uses requiring permission",
        bullets: [
          "commercial reproduction, republication, resale, or merchandising of site materials;",
          "removal of copyright, trademark, credit, or rights-management information;",
          "use of branding that suggests sponsorship, affiliation, or endorsement;",
          "systematic scraping or dataset creation from protected product content; and",
          "creation or distribution of confusingly similar counterfeit materials.",
        ],
      },
      {
        id: "ip-report",
        title: "Reporting an infringement",
        paragraphs: [
          "A rights owner or authorized representative should identify the protected work or mark, the exact site URL and material at issue, contact details, the basis of ownership or authority, and why the use is not authorized. Include a good-faith statement that the information is accurate. Electronic signatures are accepted.",
        ],
        email: true,
        emailSuffix: ". Use the subject “Intellectual property notice”.",
      },
      {
        id: "ip-review",
        title: "Review and response",
        paragraphs: [
          "We may request clarification, temporarily restrict material, notify the relevant contributor or supplier, preserve evidence, or remove content after a reasonable review. We do not decide private ownership disputes and may restore material if a complaint is withdrawn or shown to be mistaken.",
        ],
      },
      {
        id: "ip-misuse",
        title: "Accurate notices",
        paragraphs: [
          "Knowingly false or misleading notices can harm other people and may create legal liability. Do not submit confidential information beyond what is needed to assess the report. We may share the notice with the person responsible for the material where appropriate and lawful.",
        ],
      },
    ],
    related: [
      { to: "/terms", label: "Terms of service" },
      { to: "/legal-notice", label: "Legal notice" },
      { to: "/contact", label: "Contact client care" },
    ],
  },
  complaints: {
    eyebrow: "Legal & responsibility",
    title: "Complaints & resolution",
    intro: "If something has gone wrong, this process is designed to give your concern a clear owner, a fair review, and a useful response without limiting any legal remedy.",
    sections: [
      {
        id: "complaint-first",
        title: "Contact us first",
        paragraphs: [
          "Send your order number, the issue, relevant dates, what you have already tried, and the outcome you are seeking. Include photographs or tracking details where useful, but never send full payment card credentials.",
        ],
        email: true,
        emailSuffix: ". Use the subject “Formal complaint”.",
      },
      {
        id: "complaint-acknowledge",
        title: "Acknowledgement and timing",
        paragraphs: [
          "We aim to acknowledge a formal complaint within two business days and provide a substantive response within ten business days. Complex carrier, payment, safety, or supplier investigations can take longer; if so, we will provide an update and expected next step.",
        ],
      },
      {
        id: "complaint-review",
        title: "Fair review",
        paragraphs: [
          "We review order records, correspondence, product evidence, payment or carrier information, and the policies in effect when the order was placed. A person not responsible for the original decision will review an escalation when reasonably possible.",
        ],
      },
      {
        id: "complaint-outcomes",
        title: "Possible outcomes",
        paragraphs: [
          "Depending on the facts and applicable law, an outcome may include an explanation, correction, apology, repair, replacement, refund, store credit accepted by you, process improvement, or confirmation that the original decision stands with reasons.",
        ],
      },
      {
        id: "complaint-escalate",
        title: "Escalation",
        paragraphs: [
          `If you disagree with the response, reply within 30 days and identify what should be reconsidered. You may also contact an applicable consumer, privacy, payment, or court authority. For privacy matters, use our privacy request route so the appropriate process and deadlines can be applied.`,
        ],
        links: [{ prefix: "See ", to: "/privacy-rights", label: "privacy choices & requests", suffix: "." }],
      },
      {
        id: "complaint-rights",
        title: "No waiver of rights",
        paragraphs: [
          "Using this process is optional and does not suspend or waive a chargeback deadline, limitation period, statutory guarantee, regulatory complaint, or other non-excludable right. We do not require confidential arbitration through this policy.",
        ],
      },
    ],
    related: [
      { to: "/returns", label: "Returns & exchanges" },
      { to: "/privacy-rights", label: "Privacy requests" },
      { to: "/terms", label: "Terms of service" },
    ],
  },
  productSafety: {
    eyebrow: "Legal & responsibility",
    title: "Product safety & recalls",
    intro: "Safe, informed use matters. This policy explains how to use product information, report an incident, and receive notice if corrective action is required.",
    sections: [
      {
        id: "safety-information",
        title: "Read product information",
        paragraphs: [
          "Review dimensions, materials, age guidance, warnings, assembly or compatibility information, and care instructions before use. Product pages and packaging may contain item-specific directions that take priority over general guidance here.",
        ],
      },
      {
        id: "safety-use",
        title: "Safe use and storage",
        paragraphs: [
          "Use products only for their ordinary or stated purpose. Keep small parts, sharp tools, magnets, inks, adhesives, cords, and packaging away from children or pets where they may present a hazard. Stop using an item that is damaged, unusually hot, leaking, unstable, or otherwise appears unsafe.",
        ],
      },
      {
        id: "safety-incident",
        title: "Report a safety concern",
        paragraphs: [
          "For an urgent medical or fire emergency, contact local emergency services first. Then report the product name, order number, batch or label information, incident date, description, location, and photographs if safe to take. Preserve the product and packaging unless authorities direct otherwise.",
        ],
        email: true,
        emailSuffix: ". Use the subject “Product safety”.",
      },
      {
        id: "safety-review",
        title: "Our review",
        paragraphs: [
          "We may ask for the item to be isolated or returned, seek additional information, consult a supplier or authority, suspend sales, notify other customers, or arrange testing. Personal information from a report is used for safety review, regulatory duties, and appropriate follow-up.",
        ],
      },
      {
        id: "safety-recalls",
        title: "Recalls and corrective action",
        paragraphs: [
          "If a recall, warning, repair, replacement, refund, or disposal instruction is required, we will publish or send a notice describing affected products, the risk, how to identify the item, and the action to take. Follow the notice even if the product appears undamaged.",
        ],
      },
      {
        id: "safety-rights",
        title: "Customer remedies",
        paragraphs: [
          "This policy does not limit rights relating to unsafe, faulty, or misdescribed goods. We do not require a customer to pay ordinary return costs for a confirmed safety recall where we or applicable law require the return.",
        ],
      },
    ],
    related: [
      { to: "/warranty", label: "Product warranty" },
      { to: "/returns", label: "Returns & exchanges" },
      { to: "/contact", label: "Contact client care" },
    ],
  },
  security: {
    eyebrow: "Privacy & digital",
    title: "Security disclosure",
    intro: "We welcome careful, good-faith reports that help protect customers and the storefront. This policy sets boundaries for reporting a suspected technical vulnerability.",
    sections: [
      {
        id: "security-report",
        title: "How to report",
        paragraphs: [
          "Describe the affected URL or component, the potential impact, reproducible steps, relevant request or response details with secrets removed, and a safe way to contact you. Encrypt sensitive evidence where practical and do not attach malware.",
        ],
        email: true,
        emailSuffix: ". Use the subject “Security disclosure”.",
      },
      {
        id: "security-good-faith",
        title: "Good-faith research",
        bullets: [
          "use only accounts and data you own or have explicit permission to test;",
          "stop when you encounter personal, payment, confidential, or third-party data;",
          "use the minimum testing needed to demonstrate the issue;",
          "avoid disruption, denial of service, social engineering, spam, and physical testing; and",
          "give us reasonable time to investigate before any public disclosure.",
        ],
      },
      {
        id: "security-prohibited",
        title: "Activities not authorized",
        paragraphs: [
          "This policy does not authorize access beyond what is lawfully available to you, data extraction, credential testing, payment fraud, persistence, destructive actions, privacy violations, supplier-system testing, or breach of any applicable law. Ask before proceeding if a test could affect other people or services.",
        ],
      },
      {
        id: "security-response",
        title: "What to expect",
        paragraphs: [
          "We aim to acknowledge a useful report within five business days. We may request clarification, reproduce the issue, coordinate with a service provider, prioritize remediation by risk, and tell you when the review is complete. Resolution timing varies and we cannot promise a reward or public credit.",
        ],
      },
      {
        id: "security-data",
        title: "Handling discovered data",
        paragraphs: [
          "Do not copy, retain, use, or share personal data discovered during research. Tell us what was accessed and securely delete local copies after we confirm receipt, except where law requires preservation.",
        ],
      },
      {
        id: "security-customer",
        title: "Customer account or payment concerns",
        paragraphs: [
          "This channel is for technical vulnerabilities. If you believe an order, payment method, email, or gift-card code was used without permission, contact client care and your payment issuer immediately.",
        ],
      },
    ],
    related: [
      { to: "/privacy", label: "Privacy policy" },
      { to: "/privacy-rights", label: "Privacy requests" },
      { to: "/payments", label: "Payments & billing" },
    ],
  },
  sustainability: {
    eyebrow: "Legal & responsibility",
    title: "Packaging & sustainability",
    intro: "This policy explains how we communicate environmental information without overstating impact, and how customers can make informed care and disposal choices.",
    sections: [
      {
        id: "sustainability-claims",
        title: "Careful environmental claims",
        paragraphs: [
          "Material, recycled-content, certification, recyclability, or sourcing claims apply only to the product or component specifically identified. We do not describe a product as impact-free, fully sustainable, or recyclable everywhere when that cannot be substantiated.",
        ],
      },
      {
        id: "sustainability-packaging",
        title: "Packaging choices",
        paragraphs: [
          "We seek packaging that protects the order while avoiding unnecessary volume and components. Recyclable or recycled-content statements depend on the exact material and local facilities; labels or product information should be checked before disposal.",
        ],
      },
      {
        id: "sustainability-care",
        title: "Use, care, and longevity",
        paragraphs: [
          "Using refillable systems, selecting compatible inserts, following care instructions, and repairing a serviceable item can extend useful life. Care guidance is not a condition that removes remedies for a product that was faulty when supplied.",
        ],
      },
      {
        id: "sustainability-disposal",
        title: "Reuse and disposal",
        paragraphs: [
          "Separate mixed materials only when it is safe and practical. Paper affected by coatings, adhesives, laminates, metal hardware, or local contamination rules may require a different waste stream. Follow municipal guidance rather than relying on a generic recycling symbol alone.",
        ],
      },
      {
        id: "sustainability-delivery",
        title: "Delivery and returns",
        paragraphs: [
          "Where operationally practical, we consolidate items into fewer parcels. We do not discourage a legitimate return or safety report for environmental reasons. Customers should use appropriately sized, protective packaging for returns to reduce damage and repeat transport.",
        ],
      },
      {
        id: "sustainability-feedback",
        title: "Questions and corrections",
        paragraphs: [
          "Environmental information can change with materials, suppliers, and local infrastructure. If you need evidence for a specific claim or believe a statement is inaccurate, contact us with the product and wording concerned so we can review it.",
        ],
        email: true,
      },
    ],
    related: [
      { to: "/ethical-sourcing", label: "Ethical sourcing" },
      { to: "/warranty", label: "Product warranty" },
      { to: "/shipping", label: "Shipping information" },
    ],
  },
  ethicalSourcing: {
    eyebrow: "Legal & responsibility",
    title: "Supplier & ethical sourcing",
    intro: "This policy sets the minimum conduct we expect when selecting and working with suppliers. It is a standard for decisions and improvement, not a claim that every risk can be eliminated.",
    sections: [
      {
        id: "sourcing-scope",
        title: "Scope and expectations",
        paragraphs: [
          "We expect suppliers and subcontractors involved in our products or packaging to follow applicable labor, wage, working-time, health and safety, environmental, anti-bribery, sanctions, and trade laws.",
        ],
      },
      {
        id: "sourcing-labor",
        title: "Freely chosen work",
        paragraphs: [
          "Forced labor, trafficking, prison labor that is not freely chosen, recruitment-fee debt, retention of identity documents, and unlawful child labor are not acceptable. Workers should be free to leave employment subject to lawful notice and should not face discrimination, harassment, or abuse.",
        ],
      },
      {
        id: "sourcing-conditions",
        title: "Working conditions",
        paragraphs: [
          "Suppliers should provide lawful pay and benefits, reasonable working hours and rest, safe facilities, emergency procedures, appropriate protective equipment, and a way for workers to raise concerns without retaliation.",
        ],
      },
      {
        id: "sourcing-materials",
        title: "Materials and traceability",
        paragraphs: [
          "We seek accurate information about material composition, origin, regulated substances, and any certification claim. Suppliers must not substitute materials or use another party’s intellectual property without authorization.",
        ],
      },
      {
        id: "sourcing-review",
        title: "Risk review and remediation",
        paragraphs: [
          "We may request records, declarations, testing, corrective actions, or additional traceability based on product, material, geography, or reported risk. Where a problem is found, responsible remediation and prevention are preferred when they can protect affected people; severe or uncorrected issues may lead to suspension or termination.",
        ],
      },
      {
        id: "sourcing-report",
        title: "Raising a concern",
        paragraphs: [
          "Workers, suppliers, customers, and other parties may report a credible sourcing concern. Include available facts and avoid obtaining evidence unlawfully. We will limit disclosure to those who need it for review and will not knowingly retaliate against a good-faith reporter.",
        ],
        email: true,
        emailSuffix: ". Use the subject “Sourcing concern”.",
      },
    ],
    related: [
      { to: "/sustainability", label: "Sustainability" },
      { to: "/product-safety", label: "Product safety" },
      { to: "/intellectual-property", label: "IP notices" },
    ],
  },
  legalNotice: {
    eyebrow: "Legal & responsibility",
    title: "Legal notice",
    updated: "October 23, 2026",
    intro: `This website is presented under the ${brand} brand. This notice identifies the available operator contact, the status of site information, and how formal communications are handled.`,
    sections: [
      {
        id: "legal-operator",
        title: "Store identity and contact",
        paragraphs: [
          `${brand} operates this online storefront from ${siteConfig.operatingRegion.country}. Customer, privacy, accessibility, intellectual-property, security, and formal legal notices may be sent to the email below. Please identify the nature of the notice in the subject line.`,
          "For a formal notice that requires postal delivery or additional operator details under applicable law, contact us to request the current verified service instructions. We will not ask you to send payment card credentials with a legal notice.",
        ],
        email: true,
      },
      {
        id: "legal-contract",
        title: "Contract information",
        paragraphs: [
          "Product pages, the checkout summary, order messages, and the policies in effect when you order form the relevant pre-contract information. English is the current storefront and contractual language. You can save or print policies and order confirmations for your records.",
        ],
      },
      {
        id: "legal-information",
        title: "Site information",
        paragraphs: [
          "We take reasonable care with product, price, availability, compatibility, and delivery information, but correctable errors can occur. General editorial or planning content is provided for information and is not professional, legal, financial, medical, or other regulated advice.",
        ],
      },
      {
        id: "legal-links",
        title: "Third-party services and links",
        paragraphs: [
          "Payment, delivery, map, social, or other third-party services may have their own terms and privacy notices. A link or technical integration does not transfer responsibility for the third party’s independent content, and it does not imply endorsement unless we clearly say so.",
        ],
      },
      {
        id: "legal-law",
        title: "Governing framework",
        paragraphs: [
          `The storefront is managed from ${siteConfig.operatingRegion.country}. Our terms select Hong Kong law where that choice is valid. This does not deprive a consumer of mandatory protections or access to a competent court or authority that cannot lawfully be excluded in the consumer’s place of residence.`,
        ],
      },
      {
        id: "legal-notices",
        title: "Formal notices",
        paragraphs: [
          "A notice should include your full name, relevant order or matter, preferred contact details, and the action requested. Receipt of an automated or ordinary email response does not waive formal-service requirements that apply to court or regulatory documents.",
        ],
      },
    ],
    related: [
      { to: "/terms", label: "Terms of service" },
      { to: "/privacy", label: "Privacy policy" },
      { to: "/complaints", label: "Complaints process" },
    ],
  },
};

export function StorePolicy({ policyKey }) {
  return <PolicyTemplate policy={storePolicies[policyKey]} />;
}

export function PolicyCenter() {
  return (
    <main className="policy-center" id="main-content">
      <header className="policy-hero">
        <p className="policy-eyebrow">Customer care</p>
        <h1>Policy center</h1>
        <p className="policy-updated">Last updated October 23, 2026</p>
        <p className="policy-intro">
          Clear information for shopping with {brand}. These policies are
          written for this storefront and preserve any mandatory rights that
          apply where you live.
        </p>
      </header>

      <div className="policy-center__content">
        <div className="policy-center__notice">
          <p>
            Need help choosing the right policy? Email
            {" "}<a href={`mailto:${email}`}>{email}</a> with your order number
            or question. Do not include full payment card details.
          </p>
        </div>

        {policyGroups.map((group, groupIndex) => (
          <section className="policy-center__group" key={group.title}>
            <div className="policy-center__heading">
              <span>{String(groupIndex + 1).padStart(2, "0")}</span>
              <div>
                <h2>{group.title}</h2>
                <p>{group.description}</p>
              </div>
            </div>
            <div className="policy-center__grid">
              {group.items.map((item) => (
                <Link to={item.to} key={item.to} onClick={toTop}>
                  <h3>{item.label}</h3>
                  <p>{item.summary}</p>
                  <span aria-hidden="true">Read policy &rarr;</span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

export default StorePolicy;
